import { ThemeButton, ThemeInput, ThemeSelect, ThemeTextarea } from './ThemeWrapper';
import SearchableSelect from './Form/SearchableSelect';
import { PDK_EXCLUSIVE } from '../domain/userCreationRequest/onboardingFormOptions';

function Field({ label, required, hint, htmlFor, children }) {
  return (
    <div className="mb-4">
      <label htmlFor={htmlFor} className="block text-sm font-medium theme-text mb-1">
        {label}
        {required ? ' *' : ''}
      </label>
      {hint ? <p className="text-xs theme-text-secondary mb-2">{hint}</p> : null}
      {children}
    </div>
  );
}

function YesNoField({ name, label, hint, value, onChange, required = true }) {
  const yesId = `${name}-yes`;
  const noId = `${name}-no`;
  return (
    <fieldset className="mb-4">
      <legend className="block text-sm font-medium theme-text mb-1">
        {label}
        {required ? ' *' : ''}
      </legend>
      {hint ? <p className="text-xs theme-text-secondary mb-2">{hint}</p> : null}
      <div className="flex gap-4">
        <label htmlFor={yesId} className="inline-flex items-center gap-2 theme-text text-sm">
          <input
            id={yesId}
            type="radio"
            name={name}
            value="yes"
            checked={value === 'yes'}
            onChange={onChange}
            required={required}
          />
          Yes
        </label>
        <label htmlFor={noId} className="inline-flex items-center gap-2 theme-text text-sm">
          <input
            id={noId}
            type="radio"
            name={name}
            value="no"
            checked={value === 'no'}
            onChange={onChange}
          />
          No
        </label>
      </div>
    </fieldset>
  );
}

function ExclusiveCheckboxGroup({
  name,
  label,
  required,
  options,
  values,
  exclusiveId,
  onToggle,
}) {
  return (
    <fieldset className="mb-4">
      <legend className="block text-sm font-medium theme-text mb-2">
        {label}
        {required ? ' *' : ''}
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((option) => {
          const id = `${name}-${option}`.replace(/\s+/g, '-');
          return (
            <label key={option} htmlFor={id} className="inline-flex items-start gap-2 theme-text text-sm">
              <input
                id={id}
                type="checkbox"
                checked={values.includes(option)}
                onChange={() => onToggle(name, option, exclusiveId)}
              />
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export default function VeOnboardingForm({
  formData,
  form,
  categories,
  loading,
  submitting,
  error,
  success,
  disabled,
  accessNotRequired,
  onChange,
  onToggleList,
  submit,
}) {
  const durationMin = form.toolAccessDurationMin || 1;
  const durationMax = form.toolAccessDurationMax || 26;
  const phonePrefix = form.defaultPhonePrefix || '+91';

  if (loading) {
    return <p className="theme-text-secondary">Loading form…</p>;
  }

  if (disabled) {
    return (
      <div className="p-3 rounded text-sm" style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }}>
        {error}
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Title" required htmlFor="onboard-title">
          <ThemeSelect
            id="onboard-title"
            name="title"
            value={formData.title}
            onChange={onChange}
            className="w-full"
            required
          >
            <option value="">Select</option>
            {(form.titles || []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </ThemeSelect>
        </Field>
        <Field label="First name" required htmlFor="onboard-first">
          <ThemeInput
            id="onboard-first"
            name="firstName"
            value={formData.firstName}
            onChange={onChange}
            className="w-full"
            required
          />
        </Field>
        <Field label="Last name" required htmlFor="onboard-last">
          <ThemeInput
            id="onboard-last"
            name="lastName"
            value={formData.lastName}
            onChange={onChange}
            className="w-full"
            required
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Email" required htmlFor="onboard-email">
          <ThemeInput
            id="onboard-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={onChange}
            className="w-full"
            required
          />
        </Field>
        <Field label="Phone" required htmlFor="onboard-phone">
          <div className="flex">
            <span className="inline-flex items-center px-3 rounded-l-lg border-2 theme-text-secondary" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
              {phonePrefix}
            </span>
            <ThemeInput
              id="onboard-phone"
              name="phone"
              inputMode="numeric"
              autoComplete="tel"
              value={formData.phone}
              onChange={onChange}
              className="w-full rounded-l-none"
              maxLength={10}
              required
            />
          </div>
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Course category" required htmlFor="onboard-category">
          {categories.length === 0 ? (
            <p className="text-sm theme-text-secondary">No active course categories are configured.</p>
          ) : (
            <SearchableSelect
              id="onboard-category"
              name="courseCategoryId"
              value={formData.courseCategoryId}
              onChange={onChange}
              className="w-full"
              placeholder="Select category"
              options={categories}
              getOptionValue={(item) => String(item.id)}
              getOptionLabel={(item) =>
                item.categoryCode ? `${item.categoryName} (${item.categoryCode})` : item.categoryName
              }
              required
            />
          )}
        </Field>
        <Field label="Mode of batch" required htmlFor="onboard-mode">
          <ThemeSelect
            id="onboard-mode"
            name="modeOfBatch"
            value={formData.modeOfBatch}
            onChange={onChange}
            className="w-full"
            required
          >
            <option value="">Select</option>
            {(form.batchModes || []).map((item) => (
              <option key={item.id} value={item.id}>
                {item.label}
              </option>
            ))}
          </ThemeSelect>
        </Field>
        <Field label="Total fees INR" required htmlFor="onboard-fees">
          <div className="flex">
            <ThemeInput
              id="onboard-fees"
              type="number"
              min="0"
              step="1"
              name="totalFeesInr"
              value={formData.totalFeesInr}
              onChange={onChange}
              className="w-full rounded-r-none"
              required
            />
            <span className="inline-flex items-center px-3 rounded-r-lg border-2 theme-text-secondary" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
              INR
            </span>
          </div>
        </Field>
      </div>

      <YesNoField
        name="needWelcomeEmail"
        label="Need to send welcome email?"
        value={formData.needWelcomeEmail}
        onChange={onChange}
      />
      <YesNoField
        name="needMyAcademyAccess"
        label="Need MyAcademy access?"
        hint="If user have myacademy access please choose No. If user don't have myacademy access and it's required, please choose YES."
        value={formData.needMyAcademyAccess}
        onChange={onChange}
      />
      <YesNoField
        name="toolAccess"
        label="Tool access"
        hint="Please specify, if there is a requirement of Synopsy Tool acess."
        value={formData.toolAccess}
        onChange={onChange}
      />

      <Field label="Tool access duration (in weeks)" required htmlFor="onboard-duration">
        <div className="flex items-center gap-3">
          <span className="text-xs theme-text-secondary whitespace-nowrap">1 Week</span>
          <input
            id="onboard-duration"
            type="range"
            name="toolAccessDurationWeeks"
            min={durationMin}
            max={durationMax}
            value={formData.toolAccessDurationWeeks}
            onChange={onChange}
            className="w-full"
          />
          <span className="text-xs theme-text-secondary whitespace-nowrap">{durationMax} Week</span>
          <ThemeInput
            type="number"
            name="toolAccessDurationWeeks"
            min={durationMin}
            max={durationMax}
            value={formData.toolAccessDurationWeeks}
            onChange={onChange}
            className="w-20"
            aria-label="Tool access duration in weeks"
          />
        </div>
      </Field>

      <Field
        label="Start date for tool access"
        required
        htmlFor="onboard-start"
        hint="dd-MMM-yyyy"
      >
        <ThemeInput
          id="onboard-start"
          type="date"
          name="toolAccessStartDate"
          value={formData.toolAccessStartDate}
          onChange={onChange}
          className="w-full md:w-64"
          required
        />
      </Field>

      <YesNoField
        name="specialMachineVnc"
        label="Requirement of special machine (VNC only)?"
        value={formData.specialMachineVnc}
        onChange={onChange}
      />

      <ExclusiveCheckboxGroup
        name="vncTools"
        label="List of tool access (VNC machine)"
        required
        options={form.vncTools || []}
        values={formData.vncTools}
        exclusiveId={accessNotRequired}
        onToggle={onToggleList}
      />
      <ExclusiveCheckboxGroup
        name="puttyTools"
        label="List of tool access (Putty machine)"
        required
        options={form.puttyTools || []}
        values={formData.puttyTools}
        exclusiveId={accessNotRequired}
        onToggle={onToggleList}
      />
      <ExclusiveCheckboxGroup
        name="specialVncTools"
        label="List of tool access (special machine - VNC only)"
        required
        options={form.specialVncTools || []}
        values={formData.specialVncTools}
        exclusiveId={accessNotRequired}
        onToggle={onToggleList}
      />
      <ExclusiveCheckboxGroup
        name="pdkGrpPermissions"
        label="PDK grp permission"
        required
        options={form.pdkGrpPermissions || []}
        values={formData.pdkGrpPermissions}
        exclusiveId={form.pdkExclusive || PDK_EXCLUSIVE}
        onToggle={onToggleList}
      />

      <Field label="Labs need to copy in home location" htmlFor="onboard-labs-copy">
        <ThemeTextarea
          id="onboard-labs-copy"
          name="labsCopyHome"
          rows={3}
          value={formData.labsCopyHome}
          onChange={onChange}
          className="w-full"
        />
      </Field>

      <ExclusiveCheckboxGroup
        name="labsAccess"
        label="Labs access (grp permission or copy in their home location)"
        required
        options={form.labsAccess || []}
        values={formData.labsAccess}
        onToggle={onToggleList}
      />

      {error ? (
        <div className="mb-4 p-3 rounded text-sm" style={{ backgroundColor: 'var(--color-error-100)', color: 'var(--color-error)' }} role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div className="mb-4 p-3 rounded text-sm theme-card theme-text" role="status">
          {success}
        </div>
      ) : null}

      <ThemeButton type="submit" disabled={submitting}>
        {submitting ? 'Submitting…' : 'Submit'}
      </ThemeButton>
    </form>
  );
}
